/* eslint-disable no-console */
import React, { ReactNode, useEffect, useState } from "react";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { launchDarklyConfig, defaultLDUser } from "../config/launchdarkly";
import { logger } from "../utils/logger";

interface LaunchDarklyProviderProps {
  children: ReactNode;
  user?: Record<string, any>;
}

// Component that will be rendered while LaunchDarkly is initializing
const LoadingComponent = () => <div></div>;

// Helper function for controlled logging
const logLaunchDarkly = (message: string, data?: any) => {
  // Only log in development or when explicitly enabled
  if (
    process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_LAUNCHDARKLY_DEBUG === "true"
  ) {
    if (data) {
      logger.log(`[LaunchDarkly] ${message}`, data);
    } else {
      logger.log(`[LaunchDarkly] ${message}`);
    }
  }
};

const LaunchDarklyProvider: React.FC<LaunchDarklyProviderProps> = ({
  children,
  user,
}) => {
  const [LDProvider, setLDProvider] = useState<React.ComponentType<any> | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);

  // Function to get authenticated user context from localStorage
  const getAuthenticatedUserContext = () => {
    try {
      // Check for tokens in cookies (primary) and localStorage (fallback)
      const hasLocalStorageToken =
        localStorage.getItem("token") || localStorage.getItem("token_secret");
      const hasCookieToken =
        document.cookie.includes("token=") ||
        document.cookie.includes("token_secret=");
      const hasToken = hasLocalStorageToken || hasCookieToken;
      const hasUserInfo = localStorage.getItem("userInfo");

      // Only proceed if user has tokens and userInfo
      // Note: We don't require token to be present immediately (it might be refreshing)
      if (hasToken && hasUserInfo) {
        const userInfo = JSON.parse(localStorage.userInfo);
        return {
          key: userInfo.id || userInfo.email || `user-${Date.now()}`,
          name: userInfo.name || userInfo.email,
          email: userInfo.email,
          custom: {
            superUser: userInfo.permission?.super_user,
            addCameras: userInfo.permission?.add_cameras,
            editCameras: userInfo.permission?.edit_cameras,
            deleteCameras: userInfo.permission?.delete_cameras,
            manageUsers: userInfo.permission?.manage_users,
            armDisarm: userInfo.permission?.arm_disarm,
            appVersion: localStorage.getItem("packageJsonVersion") || undefined,
          },
        };
      }
    } catch (error) {
      // Only log errors in development
      if (process.env.NODE_ENV === "development") {
        logger.error("Failed to parse userInfo from localStorage:", error);
      }
    }
    return null;
  };

  // Listen for userResourcesLoaded event
  useEffect(() => {
    const handleResourcesLoaded = () => {
      logLaunchDarkly("User resources loaded, proceeding with initialization");
      setResourcesLoaded(true);
    };

    // Check if resources are already loaded (for returning users)
    const isLoggedIn =
      document.cookie.includes("token=") ||
      document.cookie.includes("token_secret=");
    const hasUserInfo = localStorage.getItem("userInfo");

    if (isLoggedIn && hasUserInfo) {
      // User is already logged in and has data, proceed immediately
      logLaunchDarkly(
        "User already authenticated, proceeding with initialization"
      );
      setResourcesLoaded(true);
    } else {
      // Wait for resources to be loaded
      window.addEventListener("userResourcesLoaded", handleResourcesLoaded);

      // Fallback: if no event fires within 5 seconds, proceed anyway
      const fallbackTimer = setTimeout(() => {
        logLaunchDarkly(
          "No userResourcesLoaded event received, proceeding with fallback initialization"
        );
        setResourcesLoaded(true);
      }, 5000);

      return () => {
        window.removeEventListener(
          "userResourcesLoaded",
          handleResourcesLoaded
        );
        clearTimeout(fallbackTimer);
      };
    }
  }, []);

  useEffect(() => {
    // Only initialize when resources are loaded
    if (!resourcesLoaded) {
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    const initializeLDProvider = async () => {
      try {
        // Check for authenticated user context first
        const authenticatedUser = getAuthenticatedUserContext();

        // Debug logging for authentication detection
        if (process.env.NODE_ENV === "development") {
          const isLoggedIn =
            document.cookie.includes("token=") ||
            document.cookie.includes("token_secret=");
          const hasUserInfo = localStorage.getItem("userInfo");
          const hasLocalStorageToken =
            localStorage.getItem("token") ||
            localStorage.getItem("token_secret");
          const hasCookieToken =
            document.cookie.includes("token=") ||
            document.cookie.includes("token_secret=");
          const hasToken = hasLocalStorageToken || hasCookieToken;

          logLaunchDarkly("Authentication check", {
            isLoggedIn,
            hasUserInfo: !!hasUserInfo,
            hasLocalStorageToken: !!hasLocalStorageToken,
            hasCookieToken: !!hasCookieToken,
            hasToken,
            authenticatedUserFound: !!authenticatedUser,
          });
        }

        // Use authenticated user if available, otherwise use provided user or default
        const userToUse = authenticatedUser || user || defaultLDUser;

        // Ensure the user object has the required 'kind' property for LDContext
        const ldUser = {
          kind: "user",
          ...userToUse,
        };

        logLaunchDarkly("Initializing with user", {
          key: "key" in ldUser ? ldUser.key : "anonymous",
          name: "name" in ldUser ? ldUser.name : "anonymous",
          email: "email" in ldUser ? ldUser.email : "anonymous",
          // Don't log sensitive custom attributes in production
          custom:
            process.env.NODE_ENV === "development" && "custom" in ldUser
              ? ldUser.custom
              : "[REDACTED]",
        });

        if (!launchDarklyConfig.clientSideID) {
          logger.error(
            "LaunchDarklyProvider: FATAL ERROR - clientSideID is empty! Check .env files and environment variable setup."
          );
          // Application might not function correctly with feature flags.
        }

        const LDProviderComponent = await asyncWithLDProvider({
          clientSideID: launchDarklyConfig.clientSideID,
          user: ldUser,
          options: launchDarklyConfig.options,
        });

        if (isMounted) {
          setLDProvider(() => LDProviderComponent);
          setIsLoading(false);
        }
      } catch (error) {
        logger.error("Failed to initialize LaunchDarkly:", error);
        // Continue rendering the app even if LaunchDarkly fails to initialize
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Add a small delay to allow localStorage to be available and check for existing user context
    const timer = setTimeout(() => {
      initializeLDProvider();
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [user, resourcesLoaded]);

  // Listen for changes to localStorage.userInfo to re-initialize when user logs in
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "userInfo" && e.newValue) {
        // Check if user is actually logged in before re-initializing
        const isLoggedIn =
          document.cookie.includes("token=") ||
          document.cookie.includes("token_secret=");
        const hasLocalStorageToken =
          localStorage.getItem("token") || localStorage.getItem("token_secret");
        const hasCookieToken =
          document.cookie.includes("token=") ||
          document.cookie.includes("token_secret=");
        const hasToken = hasLocalStorageToken || hasCookieToken;

        if (isLoggedIn && hasToken) {
          logLaunchDarkly("User context changed, re-initializing...");
          setIsLoading(true);
          setLDProvider(null);

          // Re-initialize with new user context
          const initializeWithNewUser = async () => {
            try {
              const userInfo = JSON.parse(e.newValue!);
              const ldUser = {
                kind: "user",
                key: userInfo.id || userInfo.email || `user-${Date.now()}`,
                name: userInfo.name || userInfo.email,
                email: userInfo.email,
                custom: {
                  superUser: userInfo.permission?.super_user,
                  addCameras: userInfo.permission?.add_cameras,
                  editCameras: userInfo.permission?.edit_cameras,
                  deleteCameras: userInfo.permission?.delete_cameras,
                  manageUsers: userInfo.permission?.manage_users,
                  armDisarm: userInfo.permission?.arm_disarm,
                  appVersion:
                    localStorage.getItem("packageJsonVersion") || undefined,
                },
              };

              logLaunchDarkly("Re-initializing with authenticated user", {
                key: ldUser.key,
                name: ldUser.name,
                email: ldUser.email,
                // Don't log sensitive custom attributes in production
                custom:
                  process.env.NODE_ENV === "development"
                    ? ldUser.custom
                    : "[REDACTED]",
              });

              const LDProviderComponent = await asyncWithLDProvider({
                clientSideID: launchDarklyConfig.clientSideID,
                user: ldUser,
                options: launchDarklyConfig.options,
              });

              setLDProvider(() => LDProviderComponent);
              setIsLoading(false);
            } catch (error) {
              logger.error(
                "Failed to re-initialize LaunchDarkly with new user:",
                error
              );
              setIsLoading(false);
            }
          };

          initializeWithNewUser();
        }
      }
    };

    // Listen for storage events (for cross-tab synchronization)
    window.addEventListener("storage", handleStorageChange);

    // Also check for userInfo changes on the current tab
    const checkUserInfoInterval = setInterval(() => {
      const currentUserInfo = localStorage.getItem("userInfo");
      const isLoggedIn =
        document.cookie.includes("token=") ||
        document.cookie.includes("token_secret=");
      const hasLocalStorageToken =
        localStorage.getItem("token") || localStorage.getItem("token_secret");
      const hasCookieToken =
        document.cookie.includes("token=") ||
        document.cookie.includes("token_secret=");
      const hasToken = hasLocalStorageToken || hasCookieToken;

      if (
        currentUserInfo &&
        isLoggedIn &&
        hasToken &&
        !isLoading &&
        resourcesLoaded
      ) {
        // If we have userInfo and we're not loading, we might need to re-initialize
        // This is a fallback for cases where storage event doesn't fire
        const authenticatedUser = getAuthenticatedUserContext();
        if (authenticatedUser && !LDProvider) {
          logLaunchDarkly(
            "Detected userInfo but no provider, re-initializing..."
          );
          setIsLoading(true);
          setLDProvider(null);
          // The useEffect above will handle re-initialization
        }
      }
    }, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(checkUserInfoInterval);
    };
  }, [isLoading, LDProvider, resourcesLoaded]);

  // Always render the app, even if LaunchDarkly is not ready
  // The app will work without feature flags until LaunchDarkly is initialized
  if (!LDProvider) {
    if (!resourcesLoaded) {
      logLaunchDarkly("Waiting for user resources to load...");
    }
    // Return children directly instead of LoadingComponent
    return <>{children}</>;
  }

  return <LDProvider>{children}</LDProvider>;
};

export default LaunchDarklyProvider;
